import { defineStore } from 'pinia';
import { watch } from 'vue';
import { CustomClaims, User, UserState, FirebaseFeedback, UserProjectRole } from '@/types/store';
import { OrderCartItem, CartItem, Session, Order } from "@/types/stripe";
import { getUserProjectsInfo } from '@/store/helpers/user/getUserProjectsInfo';
import { onSnapshot, collection, doc, query, where, Query, CollectionReference, WhereFilterOp, orderBy as firestoreOrderBy, Timestamp } from '@/firebase/firestore';
import orderBy from 'lodash/orderBy';
import { firestore } from '@/firebase/firestore';
import { PrivacyItem } from '@/types/privacy';
import { PreferencesItem } from '@/types/preferences';
import longProfile from "@/static/demo-user/long.json";
import authProfile from "@/static/demo-user/auth.json";
import demoOrdersData from "@/static/demo-user/orders.json";
import { FirebaseUser } from '@/firebase/user';
import { useProfileStore } from './profile.store';
import { Group } from '@/types/components';
import { UserRole } from '@/types/rbac';
import { Auth } from '@/firebase/auth';
import { validateSessionWithRefresh } from '@/store/helpers/authenticate/tokens';

type StandardizedRole = 'admin' | 'owner' | 'manager' | 'member' | 'viewer' | 'customer';

type ProjectStoreInstance = ReturnType<(typeof import('@/store/project.store'))['useProjectStore']>;

const KNOWN_STANDARD_ROLES = new Set<StandardizedRole>([
    'admin',
    'owner',
    'manager',
    'member',
    'viewer',
    'customer',
]);

const STANDARD_ROLE_TO_USER_ROLE_MAP: Record<StandardizedRole, UserRole> = {
    admin: 'admin',
    owner: 'owner',
    manager: 'owner',
    member: 'customer',
    viewer: 'viewer',
    customer: 'customer',
};

const STANDARD_ROLE_TO_PROJECT_ROLE_MAP: Record<StandardizedRole, UserProjectRole> = {
    admin: 'admin',
    owner: 'owner',
    manager: 'owner',
    member: 'member',
    viewer: 'viewer',
    customer: 'member',
};

interface RoleDetectionOptions {
    claims: CustomClaims | null;
    projectId?: string | null;
    isShirlyAdmin: boolean;
    isShirlyAdminOverride: boolean;
}

let projectRoleWatchersInitialized = false;
let projectRoleWatcherInitPromise: Promise<void> | null = null;
let cachedProjectStore: ProjectStoreInstance | null = null;

async function getProjectStoreInstance(): Promise<ProjectStoreInstance> {
    if (cachedProjectStore) {
        return cachedProjectStore;
    }
    const module = await import('@/store/project.store');
    cachedProjectStore = module.useProjectStore();
    return cachedProjectStore;
}

function normalizeRoleValue(value: unknown): StandardizedRole | null {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = value.toLowerCase().trim() as StandardizedRole;
    return KNOWN_STANDARD_ROLES.has(normalized) ? normalized : null;
}

function detectStandardRoleFromClaims(options: RoleDetectionOptions): StandardizedRole | null {
    const { claims, projectId, isShirlyAdmin, isShirlyAdminOverride } = options;
    if (!claims || !projectId) {
        return null;
    }

    const claimData = claims as Record<string, any>;

    if (claimData.demo === 'admin' && !isShirlyAdminOverride) {
        return 'admin';
    }

    if (claimData.shirly && isShirlyAdmin && !isShirlyAdminOverride) {
        return 'admin';
    }

    const directRole = normalizeRoleValue(claimData[projectId]);
    if (directRole) {
        return directRole;
    }

    const explicitRole = normalizeRoleValue(claimData[`${projectId}:role`]);
    if (explicitRole) {
        return explicitRole;
    }

    const nestedProjectRole = normalizeRoleValue(claimData.projectRoles?.[projectId]);
    if (nestedProjectRole) {
        return nestedProjectRole;
    }

    const nestedRolesRole = normalizeRoleValue(claimData.roles?.[projectId]);
    if (nestedRolesRole) {
        return nestedRolesRole;
    }

    const hasAnyRoleClaim = Object.keys(claimData).some(key =>
        key.includes(projectId) || key === 'roles' || key === 'projectRoles'
    );

    if (hasAnyRoleClaim) {
        return 'customer';
    }

    return null;
}

function mapStandardRoleToUserRole(role: StandardizedRole | null): UserRole | null {
    if (!role) {
        return null;
    }
    return STANDARD_ROLE_TO_USER_ROLE_MAP[role];
}

function mapStandardRoleToProjectRole(role: StandardizedRole | null): UserProjectRole {
    if (!role) {
        return 'unauthorized';
    }
    return STANDARD_ROLE_TO_PROJECT_ROLE_MAP[role];
}

const defaultState: UserState = {
    authUser: null,
    user: {},
    customClaims: null,
    isAuthenticated: false,
    isExplicitLogout: false,
    projectRole: 'unauthorized',
    isAdmin: false,
    userProjects: [],
    checkoutSessions: [],
    checkoutSessionsItems: {},
    orders: [],
    ordersItems: {},
    shopping_carts: {},
    privacy: [],
    preferences: [],
    boundItems: new Set<string>(),
    organizations: [],
    relatedNames: [],
    refreshUserIdToken: false,
    feedback: [],
    acknowledgements: [],
    isShirlyAdmin: false,
    currentUserGroup: null,
    // RBAC fields
    rbacRole: null,
    rbacProjectId: null,
    rbacLastUpdated: null,
    isShirlyAdminOverride: false,
    // Cross-tab synchronization
    crossTabSyncEnabled: true,
    lastCrossTabSync: null,
};

export const useUserStore = defineStore({
    id: 'user',

    state: (): UserState => ({ ...defaultState }),

    getters: {
        isDemo: (state) => {
            return state.customClaims?.demo === 'admin';
        },
        getState: (state) => <K extends keyof UserState>(key: K): UserState[K] => {
            return state[key];
        },
        getCheckoutSessionWithItems: (state) => (sessionId: string) => {
            if (!state.checkoutSessions || !sessionId) {
                return null;
            }

            const session = state.checkoutSessions.find(p => p?.session?.id === sessionId);
            if (session && session.session) {
                const items = state.checkoutSessionsItems[sessionId] || [];
                return {
                    ...session,
                    items,
                };
            }
            return null;
        },
        getCheckoutSessionById: (state) => (id: string) => {
            // This finds a session with the firestore document ID (not the Stripe session ID)
            return state.checkoutSessions.find(session => session.id === id);
        },
        getOrderWithItem: (state) => (orderId: string | null, sessionId?: string) => {
            try {
                console.log(orderId, state.orders)
                let order;
                if (orderId) {
                    order = state.orders.find(p => p.id === orderId);
                } else {
                    if (!sessionId) throw new Error('No sessionId or orderId provided');
                    order = state.orders.find(p => p.session.id === sessionId);
                }
                if (order) {
                    const effectiveOrderId = order?.id;
                    const items = state.ordersItems[effectiveOrderId] || [];
                    return {
                        ...order,
                        items,
                    };
                }
                return null;
            } catch (e) {
                console.error(e)
            }
        },
        getOrdersWithItem: (state) => () => {
            try {
                return state.orders.map(order => {
                    if (order) {
                        const effectiveOrderId = order?.id;
                        const items = state.ordersItems[effectiveOrderId] || [];
                        return {
                            ...order,
                            items,
                        };
                    }
                })
            } catch (e) {
                console.error(e)
            }
        }
    },

    actions: {
        setState<K extends keyof UserState>(payload: { key: K; value: UserState[K] }) {
            void this.ensureProjectRoleWatchers();
            const { key, value } = payload;
            this.$state[key] = value;

            // If updating organizations or relatedNames, also update the profile store
            if ((key === 'organizations' || key === 'relatedNames') && value) {
                try {
                    const profileStore = useProfileStore();
                    if (profileStore) {
                        // Only update profile store if the value can be safely cast
                        if (key === 'organizations') {
                            profileStore.setState({
                                key: 'organizations',
                                value: value as unknown as typeof profileStore.organizations
                            });
                        } else if (key === 'relatedNames') {
                            profileStore.setState({
                                key: 'relatedNames',
                                value: value as unknown as typeof profileStore.relatedNames
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error syncing data to profile store:', error);
                }
            }

            if (key === 'customClaims' || key === 'isShirlyAdmin' || key === 'isShirlyAdminOverride') {
                void this.syncProjectRoleFromClaims();
            }
        },

        setNestedState(path: string, value: any) {
            const keys = path.split('.');
            let stateRef: any = this.$state;
            keys.forEach((key, index) => {
                if (index === keys.length - 1) {
                    stateRef[key] = value;
                } else {
                    if (!stateRef[key]) {
                        this.$patch((state: any) => {
                            state[key] = {};
                        });
                    }
                    stateRef = stateRef[key];
                }
            });
        },

        async ensureProjectRoleWatchers(): Promise<void> {
            if (projectRoleWatchersInitialized) {
                return;
            }

            if (projectRoleWatcherInitPromise) {
                await projectRoleWatcherInitPromise;
                return;
            }

            projectRoleWatcherInitPromise = (async () => {
                try {
                    const projectStore = await getProjectStoreInstance();

                    watch(
                        () => projectStore.projectId,
                        (newProjectId) => {
                            void this.syncProjectRoleFromClaims(newProjectId);
                        },
                        { immediate: true }
                    );

                    watch(
                        () => [this.customClaims, this.isShirlyAdmin, this.isShirlyAdminOverride],
                        () => {
                            void this.syncProjectRoleFromClaims(projectStore.projectId);
                        },
                        { immediate: true }
                    );

                    projectRoleWatchersInitialized = true;
                } catch (error) {
                    console.error('[UserStore] Failed to initialize project role synchronization:', error);
                } finally {
                    projectRoleWatcherInitPromise = null;
                }
            })();

            if (projectRoleWatcherInitPromise) {
                await projectRoleWatcherInitPromise;
            }
        },

        async syncProjectRoleFromClaims(projectId?: string | null, claims?: CustomClaims | null): Promise<UserProjectRole> {
            await this.ensureProjectRoleWatchers();

            try {
                const projectStore = await getProjectStoreInstance();
                const targetProjectId = projectId ?? projectStore.projectId ?? null;
                const detectedRole = detectStandardRoleFromClaims({
                    claims: claims ?? this.customClaims,
                    projectId: targetProjectId,
                    isShirlyAdmin: this.isShirlyAdmin,
                    isShirlyAdminOverride: this.isShirlyAdminOverride,
                });
                const legacyRole = mapStandardRoleToProjectRole(detectedRole);

                if (legacyRole !== this.projectRole) {
                    this.$state.projectRole = legacyRole;
                }

                return legacyRole;
            } catch (error) {
                console.error('[UserStore] Error syncing project role from claims:', error);
                this.$state.projectRole = 'unauthorized';
                return 'unauthorized';
            }
        },

        setDemoUserProfile() {
            const updatedLongProfile = { ...longProfile, updatedAt: Timestamp.fromDate(new Date()), }
            this.setState({ key: 'authUser', value: authProfile as unknown as FirebaseUser })
            this.setState({ key: 'user', value: updatedLongProfile as User })
            this.setState({ key: 'isAuthenticated', value: true })
            this.setState({ key: 'customClaims', value: ({ demo: 'admin' } as unknown as CustomClaims) })

            // Process timestamp objects in mock data
            const processTimestamps = (obj: any): any => {
                if (obj === null || obj === undefined) return obj;

                if (Array.isArray(obj)) {
                    return obj.map(item => processTimestamps(item));
                }

                if (typeof obj === 'object') {
                    if (obj.__type === 'timestamp') {
                        return Timestamp.fromMillis(obj.seconds * 1000);
                    }

                    const result: any = {};
                    for (const key in obj) {
                        result[key] = processTimestamps(obj[key]);
                    }
                    return result;
                }

                return obj;
            };

            // Set mock orders data from the imported JSON file
            const mockOrders = processTimestamps(demoOrdersData.orders);
            const mockOrderItems = processTimestamps(demoOrdersData.orderItems);
            const mockSessions = processTimestamps(demoOrdersData.checkoutSessions);
            const mockSessionItems = processTimestamps(demoOrdersData.checkoutSessionItems);
            const mockShoppingCarts = processTimestamps(demoOrdersData.shoppingCarts);

            this.setState({ key: 'orders', value: mockOrders as any });
            this.setState({ key: 'ordersItems', value: mockOrderItems as any });
            this.setState({ key: 'checkoutSessions', value: mockSessions as any });
            this.setState({ key: 'checkoutSessionsItems', value: mockSessionItems as any });

            // Set shopping cart data for demo project
            this.setNestedState('shopping_carts.demo', mockShoppingCarts.demo as any);

            // Mark these collections as already bound to prevent actual binding attempts
            this.boundItems.add('orders');
            this.boundItems.add('checkoutSessions');
            this.boundItems.add('shopping_carts.demo');
        },

        async fetchUserProjects() {
            // Checking if authUser exists and is valid
            if (!this.$state.authUser || !this.$state.authUser.uid) {
                console.error('No authenticated user. Cannot fetch projects.');
                return;
            }

            try {
                console.log('[UserStore] fetchUserProjects: Starting fetch...');
                const res = await getUserProjectsInfo();
                console.log('[UserStore] fetchUserProjects: Response received:', {
                    isArray: Array.isArray(res),
                    length: Array.isArray(res) ? res.length : 'N/A',
                    type: typeof res,
                    value: res
                });
                
                if (res && Array.isArray(res)) {
                    const projectData = [...res];
                    this.setState({ key: 'userProjects', value: [...projectData] });
                    this.$state.boundItems.add('user_projects');  // Adding user_projects to boundItems
                    console.log('[UserStore] fetchUserProjects: Successfully set userProjects, count:', projectData.length);
                } else {
                    // Handle case where res is not an array (could be null, empty object, etc.)
                    console.warn('[UserStore] fetchUserProjects: Response is not an array, setting empty array');
                    this.setState({ key: 'userProjects', value: [] });
                    this.$state.boundItems.add('user_projects');  // Still mark as bound to prevent infinite retries
                }
            } catch (e) {
                console.error('[UserStore] fetchUserProjects: An error occurred while fetching projects:', e);
                // Set empty array on error to prevent infinite loading state
                this.setState({ key: 'userProjects', value: [] });
                this.$state.boundItems.add('user_projects');  // Mark as bound even on error
            }
        },

        filterUserProjectsByProjectId(projectId: string) {
            try {
                const hasUserProjects = this.$state.boundItems.has('user_projects');
                if (hasUserProjects) {
                    const userProjects = [...this.$state.userProjects];
                    const userProject = userProjects.filter(project => project.id === projectId);
                    this.setState({ key: 'userProjects', value: [...userProject] });
                }
            } catch (e) {
                console.error(e)
            }
        },

        async bindUserCollection<T>(
            collectionName: string,
            stateKey: keyof UserState,
            sortCriteria?: [string, ('asc' | 'desc')][],
            filterField?: string,
            filterOperator?: WhereFilterOp,
            filterValue?: any
        ): Promise<void> {
            // Check demo mode Firebase operation blocking
            const { useDemo } = await import('@/composables/useDemo');
      const demo = useDemo();
            
            if (demo.isDemoMode.value && !demo.shouldAllowFirebaseOperation.value) {
                console.warn(`[UserStore] Firebase operation blocked in demo mode: bindUserCollection(${collectionName})`);
                return;
            }

            const authUser = this.authUser;
            if (!authUser || !authUser.uid) {
                console.error(`No authenticated user. Cannot bind user ${collectionName}.`);
                return;
            }

            const boundKey = `${stateKey}`;
            if (this.boundItems.has(boundKey)) {
                return;
            }

            return new Promise((resolve, reject) => {
                if (!this.authUser) return;
                let firebaseRef: CollectionReference | Query = collection(firestore, 'users', this.authUser.uid, collectionName);
                if (filterField && filterValue !== undefined && filterOperator) {
                    firebaseRef = query(firebaseRef, where(filterField, filterOperator, filterValue));
                }

                onSnapshot(firebaseRef, (snapshot) => {
                    let data: T[] = [];
                    snapshot.forEach((doc) => {
                        data.push({ id: doc.id, ...doc.data() } as T);
                    });
                    if (sortCriteria) {
                        data = orderBy(data, ...sortCriteria);
                    }
                    this.setState({ key: stateKey, value: data });
                    this.boundItems.add(boundKey);
                    resolve();
                }, (error) => {
                    console.error(`Error binding user ${collectionName}:`, error);
                    reject(error);
                });
            });
        },

        async bindUserDocument(documentPath: string, stateKey: keyof UserState) {
            // Check demo mode Firebase operation blocking
            const { useDemo } = await import('@/composables/useDemo');
      const demo = useDemo();
            
            if (demo.isDemoMode.value && !demo.shouldAllowFirebaseOperation.value) {
                console.warn(`[UserStore] Firebase operation blocked in demo mode: bindUserDocument(${documentPath})`);
                return;
            }

            if (!this.authUser) return;
            const authUser = this.authUser;

            if (!authUser || !authUser.uid) {
                console.error(`No authenticated user. Cannot bind user document at path ${documentPath}.`);
                return;
            }

            const boundKey = `${stateKey}`;
            if (this.boundItems.has(boundKey)) {
                return;
            }

            const docRef = doc(firestore, 'users', this.authUser.uid, documentPath);

            onSnapshot(docRef, (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = { id: docSnapshot.id, ...docSnapshot.data() } as unknown as UserState[keyof UserState];
                    this.setState({ key: stateKey, value: data });
                    this.boundItems.add(boundKey);
                } else {
                    console.log(`Document at path ${documentPath} does not exist.`);
                }
            }, (error) => {
                console.error(`Error binding user document at path ${documentPath}:`, error);
            });
        },

        async bindUserPrivacy() {
            await this.bindUserCollection<PrivacyItem[]>(
                'privacy',
                'privacy',
                [['createdAt', 'desc']]
            );
        },

        async bindUserPreferences() {
            await this.bindUserCollection<PreferencesItem[]>(
                'preferences',
                'preferences',
                [['createdAt', 'desc']]
            );
        },

        async bindUserFeedback() {
            await this.bindUserCollection<FirebaseFeedback[]>(
                'feedback',
                'feedback',
                [['createdAt', 'desc']]
            );
        },

        async bindUserAcnowledgements() {
            await this.bindUserCollection<any[]>(
                'acknowledgements',
                'acknowledgements',
                [['createdAt', 'desc']]
            );
        },

        async bindOrders() {
            if (!this.authUser || !this.authUser.uid) {
                console.error('No authenticated user. Cannot bind orders.');
                return;
            }

            const uid = this.authUser.uid;
            const ordersRef = collection(firestore, 'users', uid, 'orders');
            const q = query(ordersRef, firestoreOrderBy('createdAt', 'desc'));

            onSnapshot(q, async (snapshot) => {
                const orders: Order[] = [];
                snapshot.forEach(async (doc) => {
                    const orderData = { id: doc.id, ...doc.data() } as Order;
                    orders.push(orderData);
                    await this.bindNestedOrderItems(orderData);
                });
                this.setState({ key: 'orders', value: orders });
            });
            this.boundItems.add('orders');
        },

        async bindNestedOrderItems(order: Order) {
            const stateKey = `ordersItems.${order.id}`;
            await this.bindUserCollectionNested<OrderCartItem[]>(
                `orders/${order.id}/items`,
                stateKey,
                [['createdAt', 'asc']]
            );
        },

        async bindNestedShoppingCartItems(projectId: string) {
            // If this item is already bound (including our demo data), return early
            const stateKey = `shopping_carts.${projectId}`;
            if (this.boundItems.has(stateKey)) {
                return;
            }

            // Special handling for demo mode
            if (projectId === 'demo' && this.customClaims?.demo === 'admin') {
                // Demo mode is active, we already set the mock data in setDemoUserProfile
                // Just mark it as bound if it's not already
                if (!this.boundItems.has(stateKey)) {
                    this.boundItems.add(stateKey);
                }
                return;
            }

            // Regular implementation for non-demo mode
            await this.bindUserCollectionNested<CartItem[]>(
                `shopping_carts/${projectId}/items`,
                stateKey, // Unique state key for each product's payment links
                [['createdAt', 'asc']]
            );
        },

        async bindCheckoutSessions() {
            console.log('[userStore] bindCheckoutSessions - Starting');
            if (this.boundItems.has('checkoutSessions')) {
                console.log('[userStore] bindCheckoutSessions - Already bound, skipping');
                return;
            }

            try {
                console.log('[userStore] bindCheckoutSessions - Binding checkout sessions');
                await this.bindUserCollection<Session>(
                    'checkout_sessions',
                    'checkoutSessions',
                    [['createdAt', 'desc']]
                );

                console.log(`[userStore] bindCheckoutSessions - Bound ${this.checkoutSessions.length} sessions`);

                // After binding sessions, bind their items asynchronously to avoid blocking
                if (this.checkoutSessions.length > 0) {
                    console.log('[userStore] bindCheckoutSessions - Has sessions, binding items in background');
                    // Bind items in parallel without awaiting
                    this.checkoutSessions.forEach(checkoutSession => {
                        if (checkoutSession) {
                            this.bindNestedCheckoutSessionItems(checkoutSession);
                        }
                    });
                } else {
                    console.log('[userStore] bindCheckoutSessions - No sessions to bind items for');
                }
            } catch (error) {
                console.error('[userStore] bindCheckoutSessions - Error binding checkout sessions:', error);
            }
        },

        async bindNestedCheckoutSessionItems(checkoutSession: Session) {
            const stateKey = `checkoutSessionsItems.${checkoutSession.session?.id}`;
            await this.bindUserCollectionNested<CartItem[]>(
                `checkout_sessions/${checkoutSession.id}/items`,
                stateKey,
                [['createdAt', 'asc']]
            );
        },

        async bindUserCollectionNested<T>(
            collectionName: string,
            nestedKey: string,
            sortCriteria?: [string, ('asc' | 'desc')][],
            filterField?: string,
            filterOperator?: WhereFilterOp,
            filterValue?: any
        ): Promise<void> {
            const authUser = this.authUser;

            if (!authUser || !authUser.uid) {
                console.error(`No authenticated user. Cannot bind user ${collectionName}.`);
                return;
            }

            const boundKey = `${nestedKey}`;
            if (this.boundItems.has(boundKey)) {
                return;
            }

            return new Promise((resolve, reject) => {
                if (!this.authUser) return;
                let firebaseRef: CollectionReference | Query = collection(firestore, 'users', this.authUser.uid, collectionName);
                if (filterField && filterValue !== undefined && filterOperator) {
                    firebaseRef = query(firebaseRef, where(filterField, filterOperator, filterValue));
                }

                onSnapshot(firebaseRef, (snapshot) => {
                    let data: T[] = [];
                    snapshot.forEach((doc) => {
                        data.push({ id: doc.id, ...doc.data() } as T);
                    });
                    if (sortCriteria) {
                        data = orderBy(data, ...sortCriteria);
                    }
                    this.setNestedState(nestedKey, data);
                    this.boundItems.add(boundKey);
                    resolve();
                }, (error) => {
                    console.error(`Error binding user ${collectionName}:`, error);
                    reject(error);
                });
            });
        },

        reset<K extends keyof UserState>(property?: K) {
            if (property) {
                // Unbind the specific property if it's currently bound
                this.boundItems.delete(property as string);

                // Reset the specific property to its default value
                this.$state[property] = defaultState[property];
                localStorage.removeItem(property as string);
            } else {
                // Clear all bound items and unsubscribes
                this.boundItems.clear();

                // Reset the entire state
                Object.assign(this.$state, defaultState);
                Object.keys(defaultState).forEach((key) => {
                    localStorage.removeItem(key);
                });
            }
        },

        // ===== RBAC METHODS =====

        /**
         * Update RBAC role for current project
         */
        updateRbacRole(projectId?: string): void {
            try {
                const targetProjectId = projectId ?? null;
                if (!targetProjectId || !this.customClaims) {
                    this.setState({ key: 'rbacRole', value: null });
                    this.setState({ key: 'rbacProjectId', value: null });
                    return;
                }

                const detectedStandardRole = detectStandardRoleFromClaims({
                    claims: this.customClaims,
                    projectId: targetProjectId,
                    isShirlyAdmin: this.isShirlyAdmin,
                    isShirlyAdminOverride: this.isShirlyAdminOverride,
                });

                const detectedRole = mapStandardRoleToUserRole(detectedStandardRole);

                this.setState({ key: 'rbacRole', value: detectedRole });
                this.setState({ key: 'rbacProjectId', value: targetProjectId });
                this.setState({ key: 'rbacLastUpdated', value: new Date() });
                void this.syncProjectRoleFromClaims(targetProjectId);

                console.log('[UserStore] RBAC role updated:', {
                    projectId: targetProjectId,
                    role: detectedRole,
                    customClaims: this.customClaims,
                });

            } catch (error) {
                console.error('[UserStore] Error updating RBAC role:', error);
                this.setState({ key: 'rbacRole', value: null });
                this.setState({ key: 'rbacProjectId', value: null });
            }
        },

        /**
         * Refresh custom claims and update RBAC role
         */
        async refreshCustomClaims(): Promise<boolean> {
            try {
                if (!this.authUser) {
                    console.warn('[UserStore] No authenticated user for claims refresh');
                    return false;
                }

                console.log('[UserStore] Refreshing custom claims...');

                // Force token refresh
                const idTokenResult = await this.authUser.getIdTokenResult(true);
                
                // Update custom claims
                this.setState({ 
                    key: 'customClaims', 
                    value: idTokenResult.claims as unknown as CustomClaims 
                });

                // Update RBAC role with fresh claims
                this.updateRbacRole();

                console.log('[UserStore] Custom claims refreshed successfully');
                return true;

            } catch (error) {
                console.error('[UserStore] Error refreshing custom claims:', error);
                return false;
            }
        },

        /**
         * Check for custom claims changes without forcing refresh
         */
        async checkForCustomClaimsChanges(): Promise<boolean> {
            try {
                if (!this.authUser) return false;

                // Get current token without forcing refresh
                const idTokenResult = await this.authUser.getIdTokenResult(false);
                const currentClaims = JSON.stringify(this.customClaims);
                const newClaims = JSON.stringify(idTokenResult.claims);

                // If claims have changed, update them
                if (currentClaims !== newClaims) {
                    console.log('[UserStore] Custom claims changed, updating...');
                    this.setState({ 
                        key: 'customClaims', 
                        value: idTokenResult.claims as unknown as CustomClaims 
                    });
                    this.updateRbacRole();
                    return true;
                }

                return false;

            } catch (error) {
                console.error('[UserStore] Error checking custom claims:', error);
                return false;
            }
        },

        /**
         * Toggle Shirly admin override for testing
         */
        toggleShirlyAdminOverride(enabled?: boolean): void {
            const newValue = enabled !== undefined ? enabled : !this.isShirlyAdminOverride;
            this.setState({ key: 'isShirlyAdminOverride', value: newValue });
            
            // Update RBAC role to reflect the change
            this.updateRbacRole();
            
            console.log('[UserStore] Shirly admin override:', newValue ? 'enabled' : 'disabled');
        },

        /**
         * Check if Shirly admin override is active
         */
        isShirlyAdminOverrideActive(): boolean {
            return this.isShirlyAdminOverride;
        },

        // ===== CROSS-TAB SYNCHRONIZATION =====
        
        /**
         * Enable cross-tab synchronization
         * Note: Actual sync implementation is handled by useCrossTabSync composable
         */
        enableCrossTabSync(): void {
            this.setState({ key: 'crossTabSyncEnabled', value: true });
            console.log('[UserStore] Cross-tab synchronization enabled');
        },

        /**
         * Disable cross-tab synchronization  
         */
        disableCrossTabSync(): void {
            this.setState({ key: 'crossTabSyncEnabled', value: false });
            console.log('[UserStore] Cross-tab synchronization disabled');
        }
    }
});
